import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, ChevronDown, ChevronUp, X } from "lucide-react";
import { api, errMsg, downloadCsv } from "./api";
import { Panel, GhostBtn, inputCls, Spinner } from "./ui";
import { STATUS_UI } from "./BikeDetail";

const fmtD = (s) => (s ? new Date(s).toLocaleDateString("en-AU", { dateStyle: "medium" }) : "—");
const PERIOD_KEYS = [["3m", "3 mo"], ["6m", "6 mo"], ["12m", "12 mo"], ["24m", "24 mo"]];
const STATUS_TEXT = { operational: "text-emerald-400", faulty: "text-[#ff2e4d]", awaiting_parts: "text-amber-400" };

export function Reliability({ onOpenBike }) {
  const [data, setData] = useState(null);
  const [sort, setSort] = useState({ key: "bike_number", dir: 1 });
  const [fStatus, setFStatus] = useState("all");
  const [fAvail, setFAvail] = useState("all");
  const [mostPeriod, setMostPeriod] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [incView, setIncView] = useState(null);

  useEffect(() => {
    api.get("/bikes-reliability").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!data) return <Spinner />;

  let rows = data.rows.filter((r) =>
    (fStatus === "all" || r.status === fStatus) &&
    (fAvail === "all" || (fAvail === "available") === !!r.available));
  const sortKey = mostPeriod || sort.key;
  const sortDir = mostPeriod ? -1 : sort.dir;
  rows = [...rows].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (av === bv) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return (av > bv ? 1 : -1) * sortDir;
  });

  const clickCount = async (bike, period) => {
    try {
      const { data: d } = await api.get(`/bikes/${bike}/incidents?period=${period}`);
      setIncView({ bike, period, ...d });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const header = (key, label) => (
    <th key={key} className="px-2 py-2 text-left cursor-pointer select-none whitespace-nowrap"
      onClick={() => { setMostPeriod(null); setSort({ key, dir: sort.key === key ? -sort.dir : -1 }); }}>
      {label} {sortKey === key ? (sortDir === 1 ? "↑" : "↓") : ""}
    </th>
  );

  const CountCell = ({ r, k, period }) => (
    <td className="px-2 py-2">
      <button data-testid={`rel-count-${r.bike_number}-${k}`} onClick={() => clickCount(r.bike_number, period)}
        className={`underline decoration-dotted ${r[k] > 0 ? "text-white" : "text-white/35"}`}>{r[k]}</button>
    </td>
  );

  return (
    <div data-testid="reliability-view">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select data-testid="rel-filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={inputCls + " !w-auto !min-h-[40px] text-sm"}>
          <option value="all">All statuses</option>
          <option value="operational">Operational</option>
          <option value="faulty">Faulty / Out of service</option>
          <option value="awaiting_parts">Awaiting parts</option>
        </select>
        <select data-testid="rel-filter-avail" value={fAvail} onChange={(e) => setFAvail(e.target.value)} className={inputCls + " !w-auto !min-h-[40px] text-sm"}>
          <option value="all">All availability</option>
          <option value="available">Available</option>
          <option value="out">Out of service</option>
        </select>
        <div className="flex items-center gap-1">
          <GhostBtn data-testid="rel-most-faults" className={mostPeriod ? "!border-[#ff2e4d] !text-[#ff2e4d]" : ""}
            onClick={() => setMostPeriod(mostPeriod ? null : "12m")}>Most faults</GhostBtn>
          {mostPeriod && PERIOD_KEYS.map(([k, l]) => (
            <button key={k} onClick={() => setMostPeriod(k)}
              className={`text-xs min-h-[40px] px-2 rounded-lg border ${mostPeriod === k ? "border-[#ff2e4d] text-[#ff2e4d]" : "border-white/15 text-white/50"}`}>{l}</button>
          ))}
        </div>
        <GhostBtn data-testid="rel-csv" className="ml-auto flex items-center gap-2"
          onClick={() => downloadCsv("bikes-reliability", {}, "bike-reliability.csv")}><Download size={16} /> CSV</GhostBtn>
      </div>
      <p className="text-xs text-white/40 mb-3">Calculated {data.calculated_at} Perth time. {data.note}</p>

      {incView && (
        <Panel className="p-4 mb-3" data-testid="rel-incidents-modal">
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm font-semibold">Bike {String(incView.bike).padStart(2, "0")} — {incView.period === "all" ? "all-time" : `last ${incView.period.replace("m", " months")}`} ({incView.count})</p>
            <button onClick={() => setIncView(null)} className="text-white/40 h-8 w-8"><X size={16} /></button>
          </div>
          {incView.incidents.map((i) => (
            <p key={i.incident_id} className="text-sm text-white/70 py-1 border-b border-white/5 last:border-0">
              {i.incident_id} — {i.description} <span className={`text-[10px] uppercase font-bold ${i.status === "open" ? "text-[#ff2e4d]" : "text-emerald-400"}`}>{i.status}</span>
              <span className="text-white/35 text-xs"> · {fmtD(i.occurred_at || i.reported_at)} · {i.reporter}</span>
            </p>
          ))}
          {incView.incidents.length === 0 && <p className="text-sm text-white/40">No faults recorded in this period.</p>}
        </Panel>
      )}

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="reliability-table">
          <thead className="text-xs uppercase tracking-wide text-white/50 border-b border-white/15">
            <tr>
              {header("bike_number", "Bike")}{header("status", "Status")}{header("available", "Avail")}
              {header("open", "Open")}{header("all_time", "All-time*")}
              {header("3m", "3 mo")}{header("6m", "6 mo")}{header("12m", "12 mo")}{header("24m", "24 mo")}
              {header("last_fault", "Last fault")}{header("last_service", "Last service")}
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const S = STATUS_UI[r.status] || STATUS_UI.operational;
              return (
                <tr key={r.bike_number} className="border-b border-white/5">
                  <td className="px-2 py-2 font-bold">{String(r.bike_number).padStart(2, "0")}</td>
                  <td className={`px-2 py-2 ${STATUS_TEXT[r.status] || "text-white/60"}`}>{S.label}</td>
                  <td className="px-2 py-2">{r.available ? <span className="text-emerald-400">Yes</span> : <span className="text-[#ff2e4d] font-semibold">No</span>}</td>
                  <td className="px-2 py-2">{r.open}</td>
                  <CountCell r={r} k="all_time" period="all" />
                  <CountCell r={r} k="3m" period="3m" />
                  <CountCell r={r} k="6m" period="6m" />
                  <CountCell r={r} k="12m" period="12m" />
                  <CountCell r={r} k="24m" period="24m" />
                  <td className="px-2 py-2 text-white/50 whitespace-nowrap">{fmtD(r.last_fault)}</td>
                  <td className="px-2 py-2 text-white/50 whitespace-nowrap">{fmtD(r.last_service)}</td>
                  <td className="px-2 py-2"><button data-testid={`rel-open-${r.bike_number}`} onClick={() => onOpenBike(r.bike_number)} className="text-xs underline text-sky-300">Open</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-2" data-testid="reliability-cards">
        {rows.map((r) => {
          const S = STATUS_UI[r.status] || STATUS_UI.operational;
          const open = expanded === r.bike_number;
          return (
            <Panel key={r.bike_number} className="p-3">
              <button className="w-full flex items-center justify-between min-h-[44px]" onClick={() => setExpanded(open ? null : r.bike_number)} data-testid={`rel-card-${r.bike_number}`}>
                <span className="font-bold">Bike {String(r.bike_number).padStart(2, "0")}</span>
                <span className={`text-xs font-bold uppercase ${STATUS_TEXT[r.status] || "text-white/60"}`}>{S.label}</span>
                <span className="text-xs text-white/50">12mo: <b className="text-white">{r["12m"]}</b></span>
                {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {open && (
                <div className="text-sm text-white/70 space-y-1 pt-2 border-t border-white/10 mt-2">
                  <p>Available: {r.available ? "Yes" : "No"} · Open faults: {r.open}</p>
                  <p className="flex flex-wrap items-center gap-x-1.5">
                    {[["all", "All-time*", r.all_time], ["3m", "3mo", r["3m"]], ["6m", "6mo", r["6m"]], ["12m", "12mo", r["12m"]], ["24m", "24mo", r["24m"]]].map(([per, lbl, val]) => (
                      <button key={per} data-testid={`rel-card-count-${r.bike_number}-${per}`} onClick={() => clickCount(r.bike_number, per)}
                        className="underline decoration-dotted min-h-[32px]">{lbl}: <b className="text-white">{val}</b></button>
                    ))}
                  </p>
                  <p className="text-xs text-white/45">Last fault: {fmtD(r.last_fault)} · Last service: {fmtD(r.last_service)}</p>
                  <button onClick={() => onOpenBike(r.bike_number)} className="text-xs underline text-sky-300 min-h-[36px]">Open full record →</button>
                </div>
              )}
            </Panel>
          );
        })}
      </div>
      <p className="text-[11px] text-white/35 mt-3">*All-time = recorded faults since records began.</p>
    </div>
  );
}

const TYPE_LABEL = { replace_part: "Part replaced", service: "Service", other: "Repair" };

export function WorkLog() {
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ bike: "", date_from: "", date_to: "", event_type: "", person: "", part: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const params = () => Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const load = () => api.get("/bikes-worklog", { params: params() }).then((r) => setItems(r.data.items)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div data-testid="worklog-view">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
        <input data-testid="wl-bike" value={f.bike} onChange={set("bike")} placeholder="Bike #" inputMode="numeric" className={inputCls + " !min-h-[40px] text-sm"} />
        <select data-testid="wl-type" value={f.event_type} onChange={set("event_type")} className={inputCls + " !min-h-[40px] text-sm"}>
          <option value="">All types</option>
          <option value="replace_part">Part replaced</option>
          <option value="service">Service</option>
          <option value="other">Other repair</option>
        </select>
        <input data-testid="wl-from" type="date" value={f.date_from} onChange={set("date_from")} className={inputCls + " !min-h-[40px] text-sm"} />
        <input data-testid="wl-to" type="date" value={f.date_to} onChange={set("date_to")} className={inputCls + " !min-h-[40px] text-sm"} />
        <input data-testid="wl-person" value={f.person} onChange={set("person")} placeholder="Person" className={inputCls + " !min-h-[40px] text-sm"} />
        <input data-testid="wl-part" value={f.part} onChange={set("part")} placeholder="Part" className={inputCls + " !min-h-[40px] text-sm"} />
      </div>
      <div className="flex gap-2 mb-4">
        <GhostBtn data-testid="wl-apply" onClick={load}>Apply filters</GhostBtn>
        <GhostBtn data-testid="wl-csv" className="flex items-center gap-2"
          onClick={() => downloadCsv("bikes-worklog", params(), "bike-repairs-services.csv")}><Download size={16} /> CSV</GhostBtn>
      </div>
      {!items ? <Spinner /> : items.length === 0 ? <p className="text-sm text-white/40">No repairs or services match.</p> : (
        <div className="space-y-2" data-testid="worklog-list">
          {items.map((i) => (
            <Panel key={i.work_id} className="p-3 text-sm">
              <p>
                <span className="font-bold">Bike {String(i.bike_number).padStart(2, "0")}</span>
                <span className={`ml-2 text-[10px] uppercase font-bold ${i.work_type === "service" ? "text-purple-400" : "text-sky-400"}`}>{TYPE_LABEL[i.work_type]}</span>
                {i.legacy && <span className="ml-1 text-[10px] text-white/35">imported</span>}
              </p>
              <p className="text-white/75">{i.description}</p>
              {i.part && <p className="text-white/50 text-xs">Part: {i.part}{i.quantity ? ` × ${i.quantity}` : ""}</p>}
              <p className="text-xs text-white/40">{i.by} · {fmtD(i.performed_at)}{i.outcome === "resolved" ? " · resolved fault" : i.outcome === "further" ? " · further work required" : ""}</p>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
